import MoneyInput from 'components/MoneyInput';
import React from 'react';
import { BooleanInput, NumberInput, useResourceContext, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  calculatedFieldPath,
  isFieldVisible,
  shouldShowMarkupsForTaxClassification,
  sources,
  type RateSettingsField,
} from 'resources/rate_settings/utils/fieldsVisibility';
import { getInherited } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { titleize } from 'inflection';
import FacilityAndProfessionalRateInput from '../position-rate-settings-form/LocationAndProfessionalRateInput';
import Heading from 'components/Heading';
import useCheckAccess from 'hooks/useCheckAccess';
import { useRateCalculations } from '../position-rate-settings-form/hooks/useRateCalculations';
import { PARENT_SETTINGS_FIELD } from '../position-rate-settings-form/constants';
import { MarkupsInputs } from './MarkupsInputs';

type ChildrenRateSettingsProps = {
  shouldShowField: (field: RateSettingsField) => boolean;
  getValues: (path: string) => unknown;
};

type RateSettingsProps = {
  source: string;
  taxClassification: 'w2.local' | 'w2.travel' | 'ten99';
  inheritedSettings: any;
  parentSettings?: any;
};

type ScalingMarginsProps = Pick<
  RateSettingsProps,
  'source' | 'taxClassification' | 'inheritedSettings' | 'parentSettings'
>;

const RateSettingsSpecificFields = ({
  source,
  shouldShowField,
  getValues,
  taxClassification,
  inheritedSettings,
  parentSettings,
}: RateSettingsProps & ChildrenRateSettingsProps) => {
  const fields = [];

  sources[taxClassification].forEach((fieldSource) => {
    if (shouldShowField(fieldSource as RateSettingsField)) {
      // TODO: improve
      if (
        [
          // rates
          'base_charge_rate_cents',
          'charge_rate_cents',
          // margins (base + computed, per classification)
          'base_margin_percent',
          'margin_percent_1099',
          'base_margin_percent_w2',
          'total_margin_percent_w2',
          'base_margin_percent_w2_tcv',
          'margin_percent_w2_tcv',
          'base_margin_percent_w2_travel',
          'margin_percent_w2_travel',
          // markups
          'invoicing_markup',
          'misc_markup',
          'operational_markup',
        ].includes(fieldSource)
      ) {
        return;
      }

      const FieldComponent = fieldSource.includes('cents') ? MoneyInput : NumberInput;
      fields.push(
        <FieldComponent
          key={`${source}.${taxClassification}.${fieldSource}`}
          source={calculatedFieldPath(`${source}.${taxClassification}`, fieldSource, getValues)}
          label={titleize(fieldSource)}
          helperText={getInherited(
            inheritedSettings,
            `${taxClassification}.${fieldSource}`,
            parentSettings,
          )}
          blankIfNull
        />,
      );
    }
  });

  return fields;
};

const ScalingMarginsInputs = ({
  source,
  taxClassification,
  inheritedSettings,
  parentSettings,
}: ScalingMarginsProps) => {
  const fields = [];
  const key = taxClassification.includes('w2') ? 'w2' : '1099';
  const resource = useResourceContext();
  const record = useRecordContext();
  const checkAccess = useCheckAccess();
  const action = record?.id ? 'update' : 'create';
  const isMPRS = resource === 'MarketPositionRateSetting';
  const isLPRS = resource === 'LocationPositionRateSetting';

  if ((taxClassification === 'w2.travel' && !isLPRS) || (!isMPRS && !isLPRS)) {
    return null;
  }

  const marginPercentGuardrailField = `margin_percent_${key}_guardrail`;
  const payoutRatePercentGuardrailField = `payout_rate_percent_${key}_guardrail`;
  const incrementalMarginPercentField = `incremental_margin_percent_${key}`;
  const sourcePrefix = record?.id && source ? source : 'settings';
  const appliedSource = `${sourcePrefix}.${taxClassification}`;

  const { validations } = useRateCalculations({ source: appliedSource, inheritedSettings });

  if (checkAccess(action, resource, marginPercentGuardrailField) && isMPRS) {
    fields.push(
      <NumberInput
        key={marginPercentGuardrailField}
        source={marginPercentGuardrailField}
        label={`Margin Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(inheritedSettings, marginPercentGuardrailField, parentSettings)}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateMarginPercentGuardrail) {
            validations.validateMarginPercentGuardrail(newValue);
          }
        }}
      />,
    );
  }

  if (checkAccess(action, resource, payoutRatePercentGuardrailField) && isMPRS) {
    fields.push(
      <NumberInput
        key={payoutRatePercentGuardrailField}
        source={payoutRatePercentGuardrailField}
        label={`Payout Rate Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(
          inheritedSettings,
          payoutRatePercentGuardrailField,
          parentSettings,
        )}
      />,
    );
  }

  if (checkAccess(action, resource, incrementalMarginPercentField) && (isMPRS || isLPRS)) {
    fields.push(
      <NumberInput
        key={incrementalMarginPercentField}
        source={incrementalMarginPercentField}
        label={`Incremental Margin Percent ${key.toUpperCase()}`}
        helperText={getInherited(inheritedSettings, incrementalMarginPercentField, parentSettings)}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateIncrementalMargin) {
            validations.validateIncrementalMargin(newValue);
          }
        }}
      />,
    );
  }

  return fields;
};

export const RateSettingsTaxClassificationInputs = ({
  source,
  inheritedSettings,
  taxClassification,
  showTitle = false,
}) => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const { getValues } = useFormContext();
  const parentSettings = getValues(PARENT_SETTINGS_FIELD);
  const effectiveInheritedSettings = inheritedSettings ?? parentSettings ?? {};
  const billingPlan = record?.location?.primary_billing_group?.plan;
  const isW2Travel = taxClassification === 'w2.travel';
  const shouldShowField = (field: RateSettingsField): boolean => {
    return isFieldVisible(field, resource);
  };
  const shouldShowMarkups = shouldShowMarkupsForTaxClassification({
    resource,
    taxClassification,
    billingPlan,
    shouldShowInvoicingMarkup: shouldShowField('invoicing_markup'),
  });

  return (
    <>
      {shouldShowField('charge_rate_8hr') && (
        <FacilityAndProfessionalRateInput
          source={`${source}.${taxClassification}`}
          inheritedSettings={effectiveInheritedSettings}
          taxClassification={taxClassification}
        />
      )}
      {shouldShowMarkups && (
        <MarkupsInputs
          source={`${source}.${taxClassification}`}
          inheritedSettings={effectiveInheritedSettings}
          parentSettings={parentSettings}
          taxClassification={taxClassification}
        />
      )}
      {RateSettingsSpecificFields({
        shouldShowField,
        getValues,
        source,
        taxClassification,
        inheritedSettings,
        parentSettings,
      })}

      {ScalingMarginsInputs({
        source,
        taxClassification,
        inheritedSettings,
        parentSettings,
      })}

      {!isW2Travel && (
        <>
          {showTitle && <Heading>Overtime Settings</Heading>}
          {shouldShowField('payout_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.payout_max_weekly_regular_hours`}
              label="Payout Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_max_weekly_regular_hours`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('payout_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.payout_seventh_day_rule_enabled`}
              label="Payout Seventh Day Rule Enabled"
              defaultValue={null}
              format={(value) => !!value}
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_seventh_day_rule_enabled`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('charge_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.charge_max_weekly_regular_hours`}
              label="Charge Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_max_weekly_regular_hours`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('charge_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.charge_seventh_day_rule_enabled`}
              label="Charge Seventh Day Rule Enabled"
              defaultValue={null}
              format={(value) => !!value}
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_seventh_day_rule_enabled`,
                parentSettings,
              )}
            />
          )}
        </>
      )}
    </>
  );
};
